(() => {
  const MODE = 'pillow_skinny_set';
  const selector = '[data-ln-skinny-hero][data-lp-set-option]';
  const attached = new WeakSet();

  const attach = (root) => {
    const namespace = window.LumiNovaPillowCommerceB;
    const controller = namespace?.controllers?.get(root);
    if (!controller) return;
    if (attached.has(controller)) {
      controller.syncQuickColors();
      return;
    }
    const script = root.querySelector('[data-lp-set-variants]');
    let variants;
    try {
      variants = JSON.parse(script?.textContent || 'null');
      if (!Array.isArray(variants) || !variants.length) return;
    } catch (_) {
      return;
    }
    attached.add(controller);
    const original = Object.fromEntries([
      'setPurchaseMode', 'syncPurchase', 'updateButtons', 'purchaseItems',
      'currentVariantDetail', 'commitVariantState', 'addSelectedVariant', 'selectGalleryMedia',
      'onClick', 'syncQuickColors'
    ].map((method) => [method, controller[method].bind(controller)]));
    const swatches = (role) => Array.from(root.querySelectorAll(`[data-lp-set-${role}-swatch]`));
    let pillowColor = swatches('pillow').find((swatch) => swatch.getAttribute('aria-pressed') === 'true')?.dataset.color || '';
    let skinnyColor = swatches('skinny').find((swatch) => swatch.getAttribute('aria-pressed') === 'true')?.dataset.color || '';
    let hasSelectedSet = false;
    const selectedPillow = () => swatches('pillow').find((swatch) => swatch.dataset.color === pillowColor);
    const selectedSkinny = () => swatches('skinny').find((swatch) => swatch.dataset.color === skinnyColor);
    const validId = (id) => Number.isSafeInteger(Number(id)) && Number(id) > 0;
    const pillowIndex = Number(root.dataset.setPillowIndex);
    const skinnyIndex = Number(root.dataset.setSkinnyIndex);

    const resolvedVariant = () => {
      const pillow = selectedPillow();
      const skinny = selectedSkinny();
      if (!pillow || !skinny || ![0, 1].includes(pillowIndex) || ![0, 1].includes(skinnyIndex) || pillowIndex === skinnyIndex) return null;
      const matches = variants.filter((variant) => Array.isArray(variant.options) && variant.options.length === 2
        && variant.options[pillowIndex] === pillow.dataset.color && variant.options[skinnyIndex] === skinny.dataset.color);
      if (matches.length !== 1 || !validId(matches[0].id) || !Number.isFinite(matches[0].price) || matches[0].price < 0) return null;
      return matches[0];
    };

    const available = (variant = resolvedVariant()) => variant?.available === true;

    const upperRole = 'skinny';
    const upperSource = (button) => controller.primarySwatches().find((swatch) => swatch.dataset.variantId === button.dataset.lpBQuickSwatch);
    const colorAvailable = (role, color) => variants.some((variant) => variant.available === true
      && variant.options?.[role === 'pillow' ? pillowIndex : skinnyIndex] === color);

    const syncUpperColors = () => {
      if (!root.isConnected || controller.abortController.signal.aborted) return;
      const setSelected = controller.mode === MODE;
      const current = setSelected
        ? (upperRole === 'pillow' ? selectedPillow() : selectedSkinny())
        : controller.selectedSwatch();
      root.querySelectorAll('[data-lp-b-quick-swatch]').forEach((button) => {
        const source = upperSource(button);
        const setSwatch = swatches(upperRole).find((swatch) => swatch.dataset.color === source?.dataset.color);
        const enabled = setSelected
          ? Boolean(setSwatch) && colorAvailable(upperRole, setSwatch.dataset.color)
          : controller.available(source);
        const selected = Boolean(source && current && source.dataset.color === current.dataset.color);
        button.setAttribute('aria-pressed', String(selected));
        button.classList.toggle('is-active', selected);
        button.disabled = controller.cartLocked || !enabled;
      });
      root.querySelectorAll('[data-lp-b-quick-name]').forEach((label) => {
        label.textContent = current?.dataset.color || '';
      });
    };

    controller.syncQuickColors = () => {
      original.syncQuickColors();
      syncUpperColors();
    };

    const mediaSource = (_role, swatch) => swatch;

    const updateHeroImage = (image, source, role) => {
      if (!image) return;
      const src = source?.dataset.mediaSrc || source?.dataset.previewMappedSrc || source?.dataset.previewSrc || '';
      if (!src) {
        image.hidden = true;
        image.removeAttribute('src');
        image.removeAttribute('srcset');
        return;
      }
      image.alt = `${root.dataset[role === 'pillow' ? 'setPillowLabel' : 'setSkinnyLabel'] || role} — ${source.dataset.color || ''}`;
      if (image.getAttribute('src') === src) return;
      image.hidden = false;
      image.onload = () => { image.hidden = false; };
      image.onerror = () => { image.hidden = true; };
      image.sizes = role === 'pillow' ? '(min-width: 750px) 44vw, 85vw' : '(min-width: 750px) 27vw, 48vw';
      if (source.dataset.mediaSrcset) image.srcset = source.dataset.mediaSrcset;
      else image.removeAttribute('srcset');
      image.src = src;
    };

    const syncSetMedia = () => {
      const selected = controller.mode === MODE;
      root.classList.toggle('is-set-selected', selected);
      const media = root.querySelector('[data-lp-set-media]');
      const gallery = root.querySelector('.ln-pillow-b-gallery');
      const productSelected = !gallery || Boolean(root.querySelector('[data-lp-b-product-thumb][aria-pressed="true"]'));
      const show = selected && productSelected && Boolean(media);
      root.classList.toggle('is-set-media-active', show);
      if (media) media.hidden = !show;
      if (!selected) return;
      const pillow = mediaSource('pillow', selectedPillow());
      const skinny = mediaSource('skinny', selectedSkinny());
      root.style.setProperty('--ln-skinny-set-accent', skinny?.dataset.skinnyBackgroundAccent || getComputedStyle(skinny || root).getPropertyValue('--ln-pillow-b-swatch').trim() || '#c8a8ae');
      if (!show) return;
      updateHeroImage(media.querySelector('[data-lp-set-pillow-image]'), pillow, 'pillow');
      updateHeroImage(media.querySelector('[data-lp-set-skinny-image]'), skinny, 'skinny');
    };

    const returnToProductMedia = () => {
      const productThumb = root.querySelector('[data-lp-b-product-thumb]');
      if (productThumb) controller.selectGalleryMedia(productThumb);
    };

    controller.selectGalleryMedia = (control) => {
      original.selectGalleryMedia(control);
      syncSetMedia();
    };

    const updatePreview = (role, swatch) => {
      const preview = root.querySelector(`[data-lp-set-${role}-preview]`);
      const placeholder = root.querySelector(`[data-lp-set-preview-placeholder="${role}"]`);
      const source = mediaSource(role, swatch);
      const src = source?.dataset.previewMappedSrc || source?.dataset.previewSrc || '';
      const color = getComputedStyle(source || root).getPropertyValue('--ln-pillow-b-swatch').trim() || '#c7c1b8';
      if (placeholder) {
        placeholder.style.setProperty('--ln-pillow-b-swatch', color);
        placeholder.hidden = false;
      }
      if (preview) {
        preview.alt = `${root.dataset[role === 'pillow' ? 'setPillowLabel' : 'setSkinnyLabel'] || role} — ${swatch?.dataset.color || ''}`;
        preview.hidden = !src;
        if (src && preview.getAttribute('src') !== src) {
          preview.onload = () => { if (placeholder) placeholder.hidden = true; };
          preview.onerror = () => { preview.hidden = true; if (placeholder) placeholder.hidden = false; };
          preview.src = src;
        } else if (src && preview.complete) {
          const loaded = preview.naturalWidth > 0;
          preview.hidden = !loaded;
          if (placeholder) placeholder.hidden = loaded;
        } else if (!src) {
          preview.removeAttribute('src');
        }
      }
    };

    const updateSummary = () => {
      const variant = resolvedVariant();
      const regular = variant?.compare_at_price > variant?.price ? variant.compare_at_price : variant?.price || 0;
      const saving = variant ? Math.max(0, regular - variant.price) : 0;
      root.querySelectorAll('[data-lp-set-price]').forEach((label) => {
        label.textContent = variant ? controller.money(variant.price) : '—';
      });
      root.querySelectorAll('[data-lp-set-regular]').forEach((label) => {
        label.hidden = !saving;
        label.textContent = controller.money(regular);
      });
      root.querySelectorAll('[data-lp-set-saving]').forEach((label) => {
        label.hidden = !saving;
        label.textContent = `${root.dataset.setSaveLabel || 'Save'} ${controller.money(saving)}`;
      });
      ['pillow', 'skinny'].forEach((role) => {
        const current = role === 'pillow' ? selectedPillow() : selectedSkinny();
        const optionIndex = role === 'pillow' ? pillowIndex : skinnyIndex;
        root.querySelectorAll(`[data-lp-set-${role}-color]`).forEach((label) => {
          label.textContent = current?.dataset.color || '';
        });
        swatches(role).forEach((swatch) => {
          const selected = swatch === current;
          const colorAvailable = variants.some((item) => item.available === true && item.options?.[optionIndex] === swatch.dataset.color);
          swatch.disabled = controller.cartLocked || !colorAvailable;
          swatch.setAttribute('aria-disabled', String(!colorAvailable));
          swatch.setAttribute('aria-pressed', String(selected));
          swatch.classList.toggle('is-active', selected);
        });
        updatePreview(role, current);
      });
      syncSetMedia();
      syncUpperColors();
    };

    controller.syncPurchase = () => {
      if (controller.mode !== MODE) {
        original.syncPurchase();
        updateSummary();
        return;
      }
      root.querySelectorAll('[data-lp-b-purchase-mode]').forEach((button) => {
        const selected = button.dataset.lpBPurchaseMode === MODE;
        button.setAttribute('aria-pressed', String(selected));
        button.setAttribute('aria-expanded', String(selected));
      });
      root.querySelectorAll('[data-lp-b-purchase-card]').forEach((card) => card.classList.toggle('is-selected', card.dataset.lpBPurchaseCard === MODE));
      root.querySelectorAll('[data-lp-b-purchase-panel]').forEach((panel) => { panel.hidden = panel.dataset.lpBPurchasePanel !== MODE; });
      const variant = resolvedVariant();
      root.querySelectorAll('[data-lp-b-variant-input]').forEach((input) => { input.value = variant?.id || ''; });
      root.querySelectorAll('[data-lp-b-availability]').forEach((label) => { label.textContent = available(variant) ? 'Available' : controller.labels().soldOut; });
      updateSummary();
      controller.syncQuickColors();
    };

    controller.setPurchaseMode = (mode) => {
      if (controller.cartLocked || namespace.cartLocked) return;
      const previous = controller.mode;
      if (mode === MODE) {
        if (!hasSelectedSet) {
          const color = controller.selectedSwatch()?.dataset.color;
          if (swatches('skinny').some((swatch) => swatch.dataset.color === color && !swatch.disabled)) skinnyColor = color;
          hasSelectedSet = true;
        }
        controller.mode = MODE;
        returnToProductMedia();
        controller.syncPurchase();
        controller.updateButtons('default');
      } else {
        original.setPurchaseMode(mode);
      }
      if (controller.mode !== previous) namespace.emit?.('purchase_option_selected', controller.currentVariantDetail());
    };

    controller.purchaseItems = () => {
      if (controller.mode !== MODE) return original.purchaseItems();
      const variant = resolvedVariant();
      return available(variant) ? [{ id: Number(variant.id), quantity: 1 }] : [];
    };

    controller.currentVariantDetail = () => {
      const detail = { ...original.currentVariantDetail(), purchaseMode: controller.mode };
      if (controller.mode !== MODE) return detail;
      const variant = resolvedVariant();
      return {
        ...detail,
        productId: root.dataset.setProductId,
        variantId: variant ? String(variant.id) : '',
        price: variant?.price ?? 0,
        color: [pillowColor, skinnyColor].filter(Boolean).join(' + '),
        selectedColors: { pillow: pillowColor, skinny: skinnyColor }
      };
    };

    controller.commitVariantState = (swatch, options) => {
      const sticky = controller.mode === MODE
        ? Array.from(root.querySelectorAll('[data-lp-b-sticky-price], [data-lp-b-sticky-button]')).map((element) => [element, element.textContent])
        : [];
      original.commitVariantState(swatch, options);
      sticky.forEach(([element, value]) => { element.textContent = value; });
    };

    controller.updateButtons = (state = 'default', message = '') => {
      if (controller.mode !== MODE) return original.updateButtons(state, message);
      const labels = controller.labels();
      const variant = resolvedVariant();
      const loading = state === 'loading' || controller.cartLocked;
      const disabled = loading || !available(variant);
      let label = `${labels.add} — ${variant ? controller.money(variant.price) : '—'}`;
      if (!selectedSkinny()) label = root.dataset.setSkinnyPrompt || labels.chooseSecond;
      else if (!variant) label = root.dataset.setUnavailableLabel || labels.soldOut;
      else if (!available(variant)) label = labels.soldOut;
      if (loading) label = labels.adding;
      root.querySelectorAll('[data-lp-b-main-atc]').forEach((button) => {
        button.disabled = disabled;
        button.textContent = label;
        button.setAttribute('aria-busy', String(loading));
      });
      root.querySelectorAll('[data-lp-b-purchase-mode]').forEach((button) => { button.disabled = controller.cartLocked; });
      root.querySelectorAll('[data-lp-b-cart-status]').forEach((status) => { status.textContent = message; });
      updateSummary();
      controller.syncQuickColors();
    };

    controller.addSelectedVariant = async (trigger, source) => {
      if (controller.mode !== MODE) return original.addSelectedVariant(trigger, source);
      if (controller.cartLocked || namespace.cartLocked || !controller.purchaseItems().length) return;
      controller.cartLocked = true;
      namespace.cartLocked = true;
      controller.updateButtons('loading');
      let error = '';
      try {
        const cart = await controller.readCart();
        if (cart.currency && cart.currency !== root.dataset.currency) {
          throw new Error('Your cart currency changed. Refresh this page before adding your Set.');
        }
      } catch (failure) {
        error = failure.message || controller.labels().error;
      } finally {
        controller.cartLocked = false;
        namespace.cartLocked = false;
        controller.updateButtons(error ? 'error' : 'default', error);
      }
      if (!error) return original.addSelectedVariant(trigger, source);
    };

    const selectSetColor = (role, swatch) => {
      if (!swatch || controller.mode !== MODE || controller.cartLocked || namespace.cartLocked || swatch.disabled) return;
      if (!colorAvailable(role, swatch.dataset.color)) return;
      if (role === 'pillow') pillowColor = swatch.dataset.color;
      else skinnyColor = swatch.dataset.color;
      returnToProductMedia();
      controller.syncPurchase();
      controller.updateButtons('default');
      namespace.emit?.('set_color_selected', {
        ...controller.currentVariantDetail(), component: role, componentColor: swatch.dataset.color
      });
    };

    controller.onClick = (event) => {
      const quick = event.target.closest?.('[data-lp-b-quick-swatch]');
      if (controller.mode === MODE && quick && root.contains(quick)) {
        if (!quick.disabled) {
          const source = upperSource(quick);
          const swatch = swatches(upperRole).find((item) => item.dataset.color === source?.dataset.color);
          selectSetColor(upperRole, swatch);
        }
        return;
      }
      original.onClick(event);
    };

    root.addEventListener('click', (event) => {
      const swatch = event.target.closest?.('[data-lp-set-pillow-swatch], [data-lp-set-skinny-swatch]');
      if (swatch && root.contains(swatch)) selectSetColor(swatch.hasAttribute('data-lp-set-pillow-swatch') ? 'pillow' : 'skinny', swatch);
    }, { signal: controller.abortController.signal });

    document.addEventListener('luminova:pillow-commerce', syncUpperColors, { signal: controller.abortController.signal });
    document.addEventListener('click', (event) => {
      const control = event.target.closest?.('[data-lp-b-quick-swatch], [data-lp-b-swatch], [data-lp-b-secondary-swatch], [data-lp-b-purchase-mode], [data-lp-b-product-thumb], [data-lp-b-gallery-media]');
      if (control && root.contains(control)) requestAnimationFrame(syncUpperColors);
    }, { signal: controller.abortController.signal });

    controller.syncPurchase();
    controller.updateButtons('default');
  };

  const boot = () => document.querySelectorAll(selector).forEach(attach);
  document.addEventListener('shopify:section:load', boot);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
